import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteSession as apiDelete,
  renameSession as apiRename,
  fetchSessions,
} from '../api/sessions';
import type { SessionInfo } from '../types/api';

const PAGE = 25;

export function useSessions() {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [justCompleted, setJustCompleted] = useState<SessionInfo | null>(null);

  // refs that must stay current inside async callbacks / intervals
  const queryRef = useRef('');
  const loadingMoreRef = useRef(false);
  const paginationOffsetRef = useRef(0);
  const prevFirstPageRef = useRef<SessionInfo[]>([]);

  // initial load on mount
  useEffect(() => {
    setLoading(true);
    fetchSessions(PAGE, 0)
      .then((data) => {
        setTotal(data.total);
        setSessions(data.sessions);
        paginationOffsetRef.current = data.sessions.length;
        prevFirstPageRef.current = data.sessions;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // polling while any session is generating
  const hasGenerating = sessions.some((s) => s.generating);
  useEffect(() => {
    if (!hasGenerating) return;
    const id = setInterval(async () => {
      const q = queryRef.current;
      try {
        const data = await fetchSessions(PAGE, 0, q || undefined);
        const newPage = data.sessions;
        setTotal(data.total);

        if (!q) {
          // detect completion for the toast
          const completed = newPage.find((s) => {
            const prev = prevFirstPageRef.current.find((p) => p.session_id === s.session_id);
            return prev?.generating && !s.generating;
          });
          if (completed) setJustCompleted(completed);
          prevFirstPageRef.current = newPage;

          // merge: update in-place + prepend brand-new sessions
          setSessions((prev) => {
            const byId = new Map(newPage.map((s) => [s.session_id, s]));
            const updated = prev.map((s) => byId.get(s.session_id) ?? s);
            const brandNew = newPage.filter(
              (s) => !prev.some((p) => p.session_id === s.session_id),
            );
            return [...brandNew, ...updated];
          });
        } else {
          setSessions(newPage);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(id);
  }, [hasGenerating]);

  // search: replaces the session list
  const search = useCallback(async (q: string) => {
    queryRef.current = q;
    setIsSearching(q !== '');
    setLoading(true);
    try {
      const data = await fetchSessions(PAGE, 0, q || undefined);
      setTotal(data.total);
      setSessions(data.sessions);
      paginationOffsetRef.current = data.sessions.length;
      if (!q) prevFirstPageRef.current = data.sessions;
    } catch {}
    setLoading(false);
  }, []);

  // infinite scroll: append next page
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || queryRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const data = await fetchSessions(PAGE, paginationOffsetRef.current);
      paginationOffsetRef.current += data.sessions.length;
      setTotal(data.total);
      setSessions((prev) => {
        const existingIds = new Set(prev.map((s) => s.session_id));
        const unique = data.sessions.filter((s) => !existingIds.has(s.session_id));
        return [...prev, ...unique];
      });
    } catch {}
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, []);

  // merge-refresh used after sending a message (preserves infinite scroll state)
  const refresh = useCallback(() => {
    const q = queryRef.current;
    fetchSessions(PAGE, 0, q || undefined)
      .then((data) => {
        setTotal(data.total);
        if (!q) {
          setSessions((prev) => {
            const byId = new Map(data.sessions.map((s) => [s.session_id, s]));
            const updated = prev.map((s) => byId.get(s.session_id) ?? s);
            const brandNew = data.sessions.filter(
              (s) => !prev.some((p) => p.session_id === s.session_id),
            );
            return [...brandNew, ...updated];
          });
          prevFirstPageRef.current = data.sessions;
        } else {
          setSessions(data.sessions);
        }
      })
      .catch(() => {});
  }, []);

  const renameSession = useCallback(async (id: string, title: string) => {
    const updated = await apiRename(id, title);
    setSessions((prev) => prev.map((s) => (s.session_id === id ? updated : s)));
  }, []);

  const deleteSession = useCallback(async (id: string) => {
    await apiDelete(id);
    setSessions((prev) => prev.filter((s) => s.session_id !== id));
    setTotal((t) => t - 1);
  }, []);

  const clearJustCompleted = useCallback(() => setJustCompleted(null), []);

  const hasMore = !isSearching && sessions.length < total;

  return {
    sessions,
    loading,
    loadingMore,
    hasMore,
    search,
    loadMore,
    refresh,
    renameSession,
    deleteSession,
    justCompleted,
    clearJustCompleted,
  };
}
